import { useTheme } from '@/context/ThemeContext';

const steps = [
  {
    num: '01',
    title: 'Audit Your Current Lead Flow',
    desc: 'We map every inbound channel, identify gaps, and quantify the revenue leakage in your current system.',
    icon: 'ri-search-eye-line',
  },
  {
    num: '02',
    title: 'Deploy Your AI System Infrastructure',
    desc: 'Your AI voice receptionist, lead capture, and automation layers are configured and activated.',
    icon: 'ri-cpu-line',
  },
  {
    num: '03',
    title: 'Integrate With Your Existing Tools',
    desc: 'We connect seamlessly to your CRM, calendar, and communication platforms—no disruption to your workflow.',
    icon: 'ri-links-line',
  },
  {
    num: '04',
    title: 'Launch and Optimize Performance',
    desc: 'Go live with full monitoring. We track every metric and continuously optimize for maximum conversion.',
    icon: 'ri-rocket-line',
  },
];

export default function ProcessSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="process"
      className={`relative py-28 md:py-36 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#050505]' : 'bg-white'}`}
    >
      <div className={`absolute top-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-[#38D6D6]/20 to-transparent' : 'bg-gradient-to-r from-transparent via-[#38D6D6]/30 to-transparent'}`} />

      {/* Background visual */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src="https://public.readdy.ai/ai/img_res/87e031bdbc39aa89f0b33a4d0d40cfcc.jpg"
          alt=""
          className={`w-full h-full object-cover object-center transition-opacity duration-500 ${isDark ? 'opacity-[0.05]' : 'opacity-[0.03]'}`}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px bg-[#38D6D6]/60"></div>
          <span className="text-xs font-grotesk text-[#38D6D6] tracking-widest uppercase">Process</span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-20">
          <h2 className={`font-sora font-800 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>
            Simple Implementation.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
              Immediate Impact.
            </span>
          </h2>
          <p className={`font-grotesk text-base max-w-sm leading-relaxed ${isDark ? 'text-white/45' : 'text-black/45'}`}>
            From audit to live system in days, not months. No disruption to your existing operations.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`group relative p-7 rounded-2xl border transition-all duration-300 hover:-translate-y-1.5 cursor-default ${
                isDark
                  ? 'bg-[#0D1117] border-white/5 hover:border-[#38D6D6]/20'
                  : 'bg-[#F5F5F7] border-black/5 hover:border-[#38D6D6]/20'
              }`}
            >
              {/* Step number */}
              <div className="font-sora font-800 text-6xl text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6]/15 to-[#4FA3FF]/15 leading-none mb-5 group-hover:from-[#38D6D6]/30 group-hover:to-[#4FA3FF]/30 transition-all">
                {step.num}
              </div>

              {/* Icon */}
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#38D6D6]/10 border border-[#38D6D6]/20 mb-5">
                <i className={`${step.icon} text-[#38D6D6] text-lg`}></i>
              </div>

              <h3 className={`font-sora font-600 text-base mb-3 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>{step.title}</h3>
              <p className={`font-grotesk text-sm leading-relaxed ${isDark ? 'text-white/45' : 'text-black/45'}`}>{step.desc}</p>

              {/* Connector arrow (not on last) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center z-10">
                  <i className={`ri-arrow-right-line text-sm ${isDark ? 'text-white/20' : 'text-black/20'}`}></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-block p-px rounded-full bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
            <button
              onClick={() => scrollTo('contact')}
              className={`px-10 py-4 rounded-full font-grotesk font-600 text-base transition-all duration-300 cursor-pointer whitespace-nowrap ${
                isDark ? 'bg-[#050505] text-white hover:bg-transparent hover:text-black' : 'bg-white text-[#1D1D1F] hover:bg-transparent hover:text-black'
              }`}
            >
              Start Your Audit
            </button>
          </div>
        </div>
      </div>

      <div className={`absolute bottom-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-white/5 to-transparent' : 'bg-gradient-to-r from-transparent via-black/5 to-transparent'}`} />
    </section>
  );
}
